import React, {Component, Fragment, Suspense, useEffect} from 'react';
import {connect, useDispatch, useSelector} from 'react-redux';
import {Redirect, Route, Switch, withRouter} from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import { CacheProvider } from '@emotion/react';
import { CssBaseline } from '@mui/material';
import theme, { cacheRtl } from './theme';

//actions
import {addPopup, getHomepage, getUserData, set_default_geo, setHomepageSecondarySliders} from 'redux/actions';
import {contentApi, useGetRecommendationsSliderQuery, useGetHotYoterStatePanelQuery} from 'redux/api/content.api';
import {buildHomepageSliders} from 'containers/new_home/buildHomepageSliders';

//css
import 'scss/app.scss';

//routing constants
import * as routes from 'constants/routes';
import {client_join, landing_page, supplier_join} from 'constants/routes';
import * as popup from './constants/popup-types';

//import components
import Splash from 'components/splash';
import Loader from 'components/common/loader';
import ScrollToTop from 'components/routing/scroll_top';
import ScrollUp from './containers/shared/scroll-up';
import FallbackUI from 'containers/error_boundary/fallback_ui';
import Bot from './components/services/bot';
import CustomerJoin from './pages/customer-join/CustomerJoin';

//Import containers
import Header from 'containers/new_header';
import Footer from 'containers/footer/footer';
import Home from 'containers/new_home';
import Page404 from 'containers/page404';
import Content from 'containers/content/content';
import Popups from 'containers/popups';
import ErrorBoundary from 'containers/error_boundary';


//begin code splitting
import Benefits from 'containers/benefits';
import BenefitPage from 'containers/benefit_page/benefit_page';
import { PresentBenefitsPage } from "containers/benefits_page/present_benefits_page";
import CommercePage from 'containers/commerce_page';
import ContactUs from 'containers/contact_us/contact_us';
import About from 'containers/about/about';
import Supplier from 'containers/supplier/supplier';
import SupplierJoin from 'containers/supplier_join/supplier-join';
import ClientJoin from 'containers/client_join/client-join';
import WalletPage from 'containers/wallet_page/wallet_page';
import MembershipPage from 'containers/membershipCheck';
import Favorites from 'containers/favorites/favorites';
import PersonalZone from 'containers/personal_zone';
import SearchResults from 'containers/search_results/results';
import Coupon from 'containers/coupon';
import ExpertPage from 'containers/experts_page';
import PaymentPage from 'containers/payment_page';
import ThanksPage from 'containers/thanks_page';
import CategoryPage from 'containers/category_page';
import Survey from 'containers/survey';
import CarLead from 'containers/car_lead';
import MailLink from 'containers/joining-mail-link';
import Magazine from 'containers/magazine';
import LegalAdvice from 'containers/legal_advice';
import AppPopup from "./components/app-popup";
import Barcode from './containers/barcode';
import Forum from './containers/forum';
import Search from "containers/search";
import {BenefitsPage} from "./containers/benefits_page";
import { HotYoterBenefitsPage } from './containers/hot_yoter';
import { getHotYoterBenefitsRoute } from './containers/hot_yoter/utils/hotYoterCategoryRoute';
import {LAST_NEWSLETTER_CLOSE_TIME, LAST_NEWSLETTER_REOPEN_DELAY_MINUTES, NEWSLETTER_INTERACTION_EVENTS} from "./constants/common";
import {cleanUpObject, hasMinutesPassed, isInIframe} from "./app/functions";
import Analytics from "./app/analytics";
import LandingPage from './pages/landing-page/LandingPage';
import BannersPage from './containers/banners_page';
import PresentsBenefitsPage from './containers/presents_benefits_page';
import LandingJoinContentPage from './containers/landing_join_content_page';
import UserInfoDemo from './pages/personal-zone/PersonalZone';

const HomepageDataLoader = () => {
    const dispatch = useDispatch();
    const userData = useSelector((store) => store.config.UserData);
    const homepageData = useSelector((store) => store.data.HomepageData);

    const {data: recommendationsSliderPayload, isSuccess, isError} = useGetRecommendationsSliderQuery(undefined, {
        skip: !userData,
    });

    useGetHotYoterStatePanelQuery(undefined, {
        skip: !userData,
    });

    useEffect(() => {
        dispatch(getHomepage());
    }, [dispatch]);

    useEffect(() => {
        if (!homepageData) {
            return;
        }

        const isRecommendationsReady = !userData || isSuccess || isError;
        if (!isRecommendationsReady) {
            return;
        }

        dispatch(setHomepageSecondarySliders(
            buildHomepageSliders(homepageData, userData, recommendationsSliderPayload)
        ));
    }, [dispatch, homepageData, userData, recommendationsSliderPayload, isSuccess, isError]);

    return null;
};

const routesToHide = [
    client_join,
    supplier_join,
    landing_page,
    'תקנון',
    'תקנון_מועדון_צרכנות_הוֹט',
    'preview_link='];

class App extends Component {

    constructor(props) {
        super(props);

        this.refresh_timeout = null;
        this.newsletterPopupTimeout = null;
        this.newsletterPopupSetup = false;
        this.newsletterPopupShown = false;
        this.hasUserInteracted = false;
        this.newsletterEligibleAt = 0;
        this.newsletterDelaySeconds = 0;
        this.newsletterInteractionHandler = null;
    }


    shouldActivateNewsletter() {
        const newsletterPopup = this.props.newsletterPopup;
        const {reOpenDelayMinutes, isActive = true} = newsletterPopup || {}
        const lastCloseTimeString = localStorage.getItem(LAST_NEWSLETTER_CLOSE_TIME)
        const storedReopenDelayMinutes = localStorage.getItem(LAST_NEWSLETTER_REOPEN_DELAY_MINUTES)
        const reopenDelayMinutes = storedReopenDelayMinutes != null
            ? Number(storedReopenDelayMinutes)
            : reOpenDelayMinutes
        const notOtherPopUps = this.props.popupsArray.length === 0

        if (routesToHide.some(path => decodeURI(window.location.href).includes(path))) {
            return false
        }

        if (isInIframe()) {
            return false;
        }

        if (notOtherPopUps && isActive && hasMinutesPassed(lastCloseTimeString, reopenDelayMinutes)) {
            return true
        }
    }

    componentDidMount() {
        const queryParams = new URLSearchParams(window.location.search);
        const isShowPopups = queryParams.size === 0 || !queryParams.has('dontShowPopups')
        this.props.getUserData(isShowPopups)    //impossible to cancel this call , without it it doesnt show anything


        window.onbeforeunload = () => {
            window.scrollTo(0, 0);
        };

        //refreshes the page if not been interacted for 10 minutes
        this.initRefreshTimeout();
        window.addEventListener('mousemove', () => {
            clearTimeout(this.refresh_timeout);
            this.initRefreshTimeout();
        });
        
        this.setupNewsletterPopup();
    }

    componentDidUpdate(prevProps) {
        if (prevProps.location.pathname !== this.props.location.pathname) {
            this.markUserInteracted();
        }

        if (prevProps.popupsArray.length > 0 && this.props.popupsArray.length === 0) {
            setTimeout(() => {
                this.tryShowNewsletterPopup();
            }, 2000);
        }

        if (!this.newsletterPopupSetup) {
            this.setupNewsletterPopup();
        }
    }

    componentWillUnmount() {
        if (this.newsletterPopupTimeout) {
            clearTimeout(this.newsletterPopupTimeout);
        }
        this.removeNewsletterInteractionListeners();
    }

    isMarketingApproved = (user) => {
        return !!user.maStatus
    }

    markUserInteracted = () => {
        if (this.newsletterPopupShown || this.hasUserInteracted) {
            return;
        }

        this.hasUserInteracted = true;
        this.newsletterEligibleAt = Date.now() + this.newsletterDelaySeconds * 1000;

        if (this.newsletterPopupTimeout) {
            clearTimeout(this.newsletterPopupTimeout);
        }
        this.newsletterPopupTimeout = setTimeout(() => {
            this.tryShowNewsletterPopup();
        }, this.newsletterDelaySeconds * 1000);
    }

    removeNewsletterInteractionListeners = () => {
        if (!this.newsletterInteractionHandler) {
            return;
        }

        NEWSLETTER_INTERACTION_EVENTS.forEach((eventName) => {
            window.removeEventListener(eventName, this.newsletterInteractionHandler);
        });
        this.newsletterInteractionHandler = null;
    }

    tryShowNewsletterPopup = () => {
        if (this.newsletterPopupShown) {
            return;
        }

        if (Date.now() < this.newsletterEligibleAt) {
            return;
        }

        if (!this.hasUserInteracted) {
            return;
        }

        if (this.isMarketingApproved(this.props.userData) || !this.shouldActivateNewsletter()) {
            return;
        }

        this.newsletterPopupShown = true;
        this.removeNewsletterInteractionListeners();
        Analytics.logSubscribeToNewsletterPresented();
        this.props.openPopUp({type: popup.NEWSLETTER, isShowNameFields: true});
    }

    setupNewsletterPopup = () => {
        if (this.newsletterPopupSetup) return;

        const secondsUntilPopupShown = this.props.newsletterPopup?.secondsUntilPopupShown;
        if (!secondsUntilPopupShown){
            return;
        }

        const delaySeconds = Number(secondsUntilPopupShown);
        if (isNaN(delaySeconds) || delaySeconds < 0) {
            return;
        }

        this.newsletterPopupSetup = true;
        this.newsletterDelaySeconds = delaySeconds;

        this.newsletterInteractionHandler = () => {
            this.markUserInteracted();
        };
        NEWSLETTER_INTERACTION_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, this.newsletterInteractionHandler, {passive: true});
        });
    }

    initRefreshTimeout = () => {

        const handler = (e) => {
            e.preventDefault()
            e.stopPropagation()
            window.location.reload();
        }

        this.refresh_timeout = setTimeout(() => {
            window.removeEventListener('click', handler)
            window.addEventListener('click', handler)
        }, 1000 * 60 * 10);
    };

    render() {
        const isAppReady = this.props.initial_requests_done
            && this.props.userRequestDone
            && this.props.isHomepageReady
            && this.props.isRecommendationsReady
            && this.props.isHotYoterStatePanelReady
            && this.props.isSlidersReady;

        const appContent = isAppReady
            ? this.renderAppContent()
            : <Splash/>;

        return (
            <>
                <HomepageDataLoader />
                {appContent}
            </>
        );
    }

    renderAppContent() {
        
        const queryParams = new URLSearchParams(window.location.search);
        const hideHeaderFooter = queryParams.has('hideHeaderFooter') && queryParams.get('hideHeaderFooter') === 'true'
        
        let showPopup = this.props.popupsArray.length > 0;

        //route specific top level class name
        let routeClassName = '';

        const spliced = this.props.location.pathname.split('/');

        for (let key in routes) {
            if (('/' + spliced[1]) === routes[key]) {
                routeClassName = key;
            }
        }

        return (
                <CacheProvider value={cacheRtl}>
                    <ThemeProvider theme={theme}>
                        <CssBaseline />
                        <div className={"App " + routeClassName}>
                            <ErrorBoundary fallback_ui={<FallbackUI/>}>
                                <Fragment>
                                    {!hideHeaderFooter && <Header/>}
                                    <Switch>
                                        <Route exact path="/">
                                            <Redirect to={routes.home}/>
                                        </Route>
                                        <Route exact path={routes.customer_join + '/:pageName?'} component={CustomerJoin}/>
                                        <Route exact path={routes.home + '/:popLogin?'} component={Home}/>
                                        <Route exact path={`${routes.home}/${routes.login}`} component={Home}/>
                                        <Route path={routes.benefits} component={(Benefits)}/>
                                        <Route path={routes.car_lead} component={(CarLead)}/>
                                        <Route path={routes.survey} component={(Survey)}/>
                                        <Route path={routes.landing_page + '/:slug'} component={LandingPage}/>
                                        <Route path={routes.mail_link + '/:hash?'} component={(MailLink)}/>
                                        <Route path={routes.category + '/:cat_id'} component={(CategoryPage)}/>
                                        <Route path={routes.payment} component={(PaymentPage)}/>
                                        <Route path={routes.personal_zone} component={(PersonalZone)}/>
                                        <Route path={routes.benefit + '/:benefitId' + routes.barcode + '/:barcodeNumber'}
                                               component={Barcode}/>
                                        <Route path={routes.benefit + '/:id'} component={(BenefitPage)}/>
                                        <Route path={routes.commerce + '/:id/:option_id'} component={(CommercePage)}/>
                                        <Route path={routes.legal_advice} component={(LegalAdvice)}/>
                                        <Route path={routes.forum} component={(Forum)}/>
                                        <Route exact path={routes.contact} component={(ContactUs)}/>
                                        <Route exact path={routes.experts} component={(ExpertPage)}/>
                                        <Route exact path={routes.about} component={(About)}/>
                                        <Route exact path={routes.supplier_join} component={(SupplierJoin)}/>
                                        <Route exact path={routes.client_join} component={(ClientJoin)}/>
                                        <Route exact path={routes.supplier} component={(Supplier)}/>
                                        <Route exact path={routes.smart_wallet} component={(WalletPage)}/>
                                        <Route exact path={routes.membership_check} component={(MembershipPage)}/>
                                        <Route exact path={routes.favorites} component={(Favorites)}/>
                                        <Route exact path={routes.coupon} component={(Coupon)}/>
                                        <Route path={routes.magazine} component={(Magazine)}/>
                                        <Route exact path={routes.thanks + '/:order_id'} component={(ThanksPage)}/>
                                        <Route path={routes.search_results + '/:query'} component={(SearchResults)}/>
                                        <Route path={routes.selected_benefits + '/:name'} component={(BenefitsPage)}/>
                                        <Route path={routes.present_benefits + '/:name'} component={(PresentBenefitsPage)}/>
                                        <Route path={routes.presents_benefits_page + '/:name'} component={PresentsBenefitsPage}/>
                                        <Route exact path={routes.hot_yoter}>
                                            <Redirect to={getHotYoterBenefitsRoute()}/>
                                        </Route>
                                        <Route path={routes.hot_yoter + '/:slug'} component={HotYoterBenefitsPage}/>
                                        <Route path={routes.banners_page + '/:name'} component={BannersPage}/>
                                        <Route path={routes.landing_join_content_page + '/:name'} component={LandingJoinContentPage}/>
                                        <Route path={routes.user_info} component={UserInfoDemo}/>
                                        <Route exact path={routes.search} component={(Search)}/>
                                        {this.props.contentRoutes.map((page, index) => <Route key={index} exact
                                                                                      path={'/' + page.title}
                                                                                      component={Content}/>)}
                                        <Route exact path={routes.noMatch} component={Page404}/>
                                        <Route component={Page404}/>
                                    </Switch>
                                    {!hideHeaderFooter && <Footer/>}
                                    <AppPopup/>
                                </Fragment>

                                {showPopup && <Popups/>}
                                <ScrollToTop/>
                                <ScrollUp/>
                                <Bot/>
                            </ErrorBoundary>
                        </div>
                    </ThemeProvider>
                </CacheProvider>
        );
    }
}

function LazyLoadComponent(Component) {
    return props => (
        <Suspense fallback={<Loader className="full-screen"/>}>
            <Component {...props} />
        </Suspense>
    );
}

//get required state from redux store and map them to the components props
const mapStateToProps = store => {
    const recQuery = contentApi.endpoints.getRecommendationsSlider.select()(store);
    const hotYoterStatePanelQuery = contentApi.endpoints.getHotYoterStatePanel.select()(store);

    return {
        userData: store.config.UserData,
        deviceState: store.state.deviceState,
        showLoader: store.state.showLoader,
        popupsArray: store.state.popupsArray,
        burgerMenuState: store.state.burgerMenuState,
        contentRoutes: store.config.ContentUrl,
        initial_requests_done: store.state.initial_requests_done,
        userRequestDone: store.state.userRequestDone,
        isHomepageReady: !!store.data.HomepageData,
        isRecommendationsReady: !store.config.UserData || recQuery.isSuccess || recQuery.isError,
        isHotYoterStatePanelReady: !store.config.UserData || hotYoterStatePanelQuery.isSuccess || hotYoterStatePanelQuery.isError,
        isSlidersReady: store.data.HomepageSecondarySliders !== false,
        categoriesArr: cleanUpObject(store.config.categoriesArr),
        parameters: store.config.Parameters,
        newsletterPopup: store.config.newsletterPopup
    }
}

const mapDispatchToProps = dispatch => {
    return {
        setGeoLocation: (payload) => dispatch(set_default_geo(payload)),
        openPopUp: (payload) => dispatch(addPopup(payload)),
        getUserData: (isShowPopups) => dispatch(getUserData(isShowPopups)),
    }
}
export default withRouter(connect(mapStateToProps, mapDispatchToProps, null, {pure: false})(App));
